import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  ClockIcon,
  UserIcon,
  TagIcon,
  StarIcon,
  DocumentTextIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { firestore } from '../../../database/firebase';
import { useAuth } from '../../../context/AuthContext';

const SessionHistory = () => {
  const { currentUser } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedSession, setSelectedSession] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!currentUser?.email) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const notesQuery = query(
      collection(firestore, 'sessionNotes'),
      where('coachEmail', '==', currentUser.email),
      orderBy('date', 'desc')
    );

    const unsubscribe = onSnapshot(
      notesQuery,
      snapshot => {
        const sessionsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSessions(sessionsData);
        setLoading(false);
      },
      error => {
        console.error('Failed to fetch sessions:', error);
        const simpleQuery = query(
          collection(firestore, 'sessionNotes'),
          where('coachEmail', '==', currentUser.email)
        );
        onSnapshot(
          simpleQuery,
          snapshot => {
            const sessionsData = snapshot.docs
              .map(doc => ({
                id: doc.id,
                ...doc.data(),
              }))
              .sort((a, b) => {
                const dateA = a.date ? new Date(a.date).getTime() : 0;
                const dateB = b.date ? new Date(b.date).getTime() : 0;
                return dateB - dateA;
              });
            setSessions(sessionsData);
            setLoading(false);
          },
          () => {
            toast.error('Failed to fetch session history');
            setLoading(false);
          }
        );
      }
    );

    return () => unsubscribe();
  }, [currentUser?.email]);

  const filteredSessions = sessions.filter(session => {
    const matchesFilter =
      filter === 'all' ||
      (filter === 'strategy' && session.type === 'strategy') ||
      (filter === 'pitch practice' && session.type === 'pitch practice') ||
      (filter === 'financial' && session.type === 'financial') ||
      (filter === 'marketing' && session.type === 'marketing');

    const matchesSearch =
      !searchTerm ||
      session.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.notes?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  const getTypeColor = type => {
    switch (type) {
      case 'strategy':
        return 'bg-blue-50 text-blue-600 border-blue-200';
      case 'pitch practice':
        return 'bg-purple-50 text-purple-600 border-purple-200';
      case 'financial':
        return 'bg-green-50 text-green-600 border-green-200';
      case 'marketing':
        return 'bg-orange-50 text-orange-600 border-orange-200';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl bg-white p-6 shadow-lg"
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"></div>
            <p className="text-black/60">Loading session history...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-white p-4 shadow-lg sm:p-6"
    >
      <div className="mb-6 flex flex-col items-center justify-between gap-4 sm:flex-row">
        <div className="flex items-center gap-3">
          <ClockIcon className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold text-black">Session History</h2>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <input
            type="text"
            placeholder="Search sessions..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="min-w-[200px] flex-1 rounded-lg border border-black/20 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:flex-none"
          />
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="rounded-lg border border-black/20 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">All Types</option>
            <option value="strategy">Strategy</option>
            <option value="pitch practice">Pitch Practice</option>
            <option value="financial">Financial</option>
            <option value="marketing">Marketing</option>
          </select>
        </div>
      </div>

      <div className="space-y-4">
        {filteredSessions.length === 0 ? (
          <div className="py-12 text-center">
            <ClockIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <p className="text-gray-600">
              {searchTerm || filter !== 'all'
                ? 'No sessions match your filters.'
                : 'No session history available.'}
            </p>
          </div>
        ) : (
          filteredSessions.map((session, index) => {
            const sessionDate = session.date
              ? new Date(session.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })
              : 'N/A';

            return (
              <motion.div
                key={session.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="rounded-lg border border-black/20 p-4 transition-colors hover:border-black/30"
              >
                <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                  <div className="flex-1">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <h4 className="font-medium text-black">
                        {session.clientName || 'Unknown Client'}
                      </h4>
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${getTypeColor(
                          session.type || 'strategy'
                        )}`}
                      >
                        <TagIcon className="h-3 w-3" />
                        {session.type || 'strategy'}
                      </span>
                      {session.rating > 0 && (
                        <div className="flex items-center gap-1">
                          <StarIcon className="h-4 w-4 text-yellow-500" />
                          <span className="text-sm font-medium">{session.rating}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-black/70">
                      <div className="flex items-center gap-1">
                        <ClockIcon className="h-4 w-4" />
                        <span>{sessionDate}</span>
                      </div>
                      {session.duration && (
                        <div className="flex items-center gap-1">
                          <ClockIcon className="h-4 w-4" />
                          <span>{session.duration}</span>
                        </div>
                      )}
                    </div>
                    {session.notes && (
                      <p className="mt-2 line-clamp-2 text-sm text-black/70">{session.notes}</p>
                    )}
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedSession(session)}
                    className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                  >
                    <EyeIcon className="h-4 w-4" />
                    View Details
                  </motion.button>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {selectedSession && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setSelectedSession(null)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-black">
                  {selectedSession.clientName || 'Client Session'}
                </h3>
                <p className="text-sm text-black/60">
                  {selectedSession.date
                    ? new Date(selectedSession.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : 'N/A'}{' '}
                  • {selectedSession.duration || 'N/A'} •{' '}
                  {selectedSession.type?.replace(/\b\w/g, l => l.toUpperCase()) || 'Strategy'}
                </p>
              </div>
              <button
                onClick={() => setSelectedSession(null)}
                className="rounded-lg p-2 hover:bg-gray-100"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-xl border border-gray-100 p-4">
                <p className="text-xs uppercase tracking-wide text-black/50">Rating</p>
                <div className="mt-2 flex items-center gap-2 text-lg font-semibold text-black">
                  <StarIcon className="h-5 w-5 text-yellow-500" />
                  {selectedSession.rating > 0 ? selectedSession.rating : 'Not rated'}
                </div>
              </div>
              <div className="rounded-xl border border-gray-100 p-4">
                <p className="text-xs uppercase tracking-wide text-black/50">Status</p>
                <span className="mt-2 inline-flex rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">
                  {selectedSession.status || 'completed'}
                </span>
              </div>
            </div>

            <div className="mb-6 rounded-2xl bg-gray-50 p-4">
              <p className="mb-3 flex items-center gap-2 text-xs uppercase tracking-wide text-black/50">
                <DocumentTextIcon className="h-4 w-4" />
                Session Notes
              </p>
              <p className="text-sm leading-relaxed text-black/80">
                {selectedSession.notes || 'No notes provided.'}
              </p>
            </div>

            {selectedSession.actionItems && selectedSession.actionItems.length > 0 && (
              <div>
                <p className="mb-3 text-xs uppercase tracking-wide text-black/50">Action Items</p>
                <ul className="space-y-2 text-sm text-black/80">
                  {selectedSession.actionItems.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-black/30" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default SessionHistory;
